import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzStatisticModule } from 'ng-zorro-antd/statistic';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { LangChangeEvent, TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgChartsModule } from 'ng2-charts';
import { ChartData, ChartOptions } from 'chart.js';
import { Subscription, timer } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { AdminDashboardService } from '../services/admin-dashboard.service';
import {
  AdminDashboardActivityResponse,
  AdminDashboardActivityItem,
  AdminBillingConfig,
  AdminDashboardBillingHistoryItem,
  AdminDashboardCharts,
  AdminDashboardCompanyDetail,
  AdminDashboardCompanyResponse,
  AdminDashboardCompanyRow,
  AdminDashboardContractSummary,
  AdminDashboardFilters,
  AdminDashboardOverview,
  BillingActionItem,
  BillingActionListResponse,
  BillingActionCountResponse,
  BillingActionType,
  ContractAlertsResponse,
  ContractTimelineResponse,
  NextContractMilestone,
} from './admin-dashboard.models';
import {
  BillingDocument,
  BillingDocumentGroup,
  BillingDocumentType,
  UpdateBillingDocumentPayload,
} from '../models/billing-document.models';
import { ContractTimelineComponent } from './contract-timeline/contract-timeline.component';
import { ContractAlertsComponent } from './contract-alerts/contract-alerts.component';

@Component({
  selector: 'app-admin-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    DatePipe,
    NzAlertModule,
    NzBadgeModule,
    NzButtonModule,
    NzCardModule,
    NzCollapseModule,
    NzDatePickerModule,
    NzDescriptionsModule,
    NzDrawerModule,
    NzEmptyModule,
    NzFormModule,
    NzGridModule,
    NzIconModule,
    NzInputModule,
    NzInputNumberModule,
    NzListModule,
    NzModalModule,
    NzPaginationModule,
    NzProgressModule,
    NzSelectModule,
    NzSkeletonModule,
    NzSpinModule,
    NzStatisticModule,
    NzSwitchModule,
    NzTableModule,
    NzTagModule,
    NzTabsModule,
    NgChartsModule,
    ContractTimelineComponent,
    ContractAlertsComponent,
  ],
  templateUrl: './admin-dashboard.component.html',
  styleUrl: './admin-dashboard.component.less',
})
export class AdminDashboardComponent implements OnInit, OnDestroy {
  providerSignatureStampPreviewUrl = '';
  uploadingProviderSignatureStamp = false;
  removingProviderSignatureStamp = false;
  filtersForm: FormGroup;
  billingForm: FormGroup;
  pricingForm: FormGroup;
  billingConfigForm: FormGroup;

  overview?: AdminDashboardOverview;
  charts?: AdminDashboardCharts;
  contracts?: AdminDashboardContractSummary;
  companies: AdminDashboardCompanyRow[] = [];
  activity: AdminDashboardActivityItem[] = [];
  billingHistory: AdminDashboardBillingHistoryItem[] = [];
  selectedCompany?: AdminDashboardCompanyDetail;
  selectedCompanyTimeline: ContractTimelineResponse | null = null;
  billingConfig?: AdminBillingConfig;
  billingDocuments: BillingDocument[] = [];
  billingDocumentsByYear: BillingDocumentGroup[] = [];
  filteredBillingDocumentsByYear: BillingDocumentGroup[] = [];
  contractAlerts: ContractAlertsResponse['items'] = [];

  customerGrowthChartData: ChartData<'line'> = { labels: [], datasets: [] };
  customerGrowthChartOptions: ChartOptions<'line'> = this.buildCartesianChartOptions<'line'>();
  arrTrendChartData: ChartData<'bar'> = { labels: [], datasets: [] };
  arrTrendChartOptions: ChartOptions<'bar'> = this.buildCartesianChartOptions<'bar'>();
  propertyTrendChartData: ChartData<'line'> = { labels: [], datasets: [] };
  propertyTrendChartOptions: ChartOptions<'line'> = this.buildCartesianChartOptions<'line'>();
  planMixChartData: ChartData<'doughnut'> = { labels: [], datasets: [] };
  planMixChartOptions: ChartOptions<'doughnut'> = this.buildDoughnutChartOptions();
  workforceChartData: ChartData<'bar'> = { labels: [], datasets: [] };
  workforceChartOptions: ChartOptions<'bar'> = this.buildStackedBarChartOptions();
  detailScansChartData: ChartData<'line'> = { labels: [], datasets: [] };
  detailScansChartOptions: ChartOptions<'line'> = this.buildCartesianChartOptions<'line'>();
  detailTimeChartData: ChartData<'line'> = { labels: [], datasets: [] };
  detailTimeChartOptions: ChartOptions<'line'> = this.buildCartesianChartOptions<'line'>();

  loadingOverview = true;
  loadingCharts = true;
  loadingCompanies = true;
  loadingContracts = true;
  loadingActivity = true;
  loadingCompanyDetail = false;
  loadingBillingDocuments = false;
  loadingBillingHistory = false;
  loadingContractTimeline = false;
  savingBillingDocument = false;
  savingPricing = false;
  loadingBillingConfig = true;
  savingBillingConfig = false;
  loadingContractAlerts = true;
  errorMessage = '';

  drawerVisible = false;
  billingModalVisible = false;
  editingBillingDocument: BillingDocument | null = null;
  selectedBillingFile: File | null = null;
  selectedBillingYearFilter: number | 'all' = 'all';
  selectedBillingTypeFilter: 'all' | BillingDocumentType = 'all';

  tablePageIndex = 1;
  tablePageSize = 10;
  tableTotal = 0;
  tableSortField = 'healthScore';
  tableSortOrder = 'ascend';
  contractsPageIndex = 1;
  contractsPageSize = 8;
  contractsSortField = 'latestContractYear';
  contractsSortOrder = 'descend';
  activityPageIndex = 1;
  activityPageSize = 8;
  activityTotal = 0;

  billingActions: BillingActionItem[] = [];
  billingActionsTotal = 0;
  billingActionsPage = 1;
  billingActionsPageSize = 20;
  billingActionsHasMore = false;
  billingActionCount: BillingActionCountResponse | null = null;
  loadingBillingActions = true;
  billingActionStatusFilter: string = 'pending';
  billingActionTypeFilter: string = 'all';
  billingActionSearch = '';
  customerSearch = '';
  billingSearch = '';
  private customerSearchTimer: ReturnType<typeof setTimeout> | null = null;
  private billingSearchTimer: ReturnType<typeof setTimeout> | null = null;
  noteModalVisible = false;
  noteActionItem: BillingActionItem | null = null;
  noteText = '';

  drawerBillingActions: BillingActionItem[] = [];
  loadingDrawerBillingActions = false;

  private subscriptions = new Subscription();
  private overviewRequestId = 0;
  private chartsRequestId = 0;
  private companiesRequestId = 0;
  private contractsRequestId = 0;
  private activityRequestId = 0;
  private companyDetailRequestId = 0;
  private billingActionRequestId = 0;

  readonly planOptions = ['all', 'free', 'pro', 'business'];
  readonly companyStatusOptions = ['all', 'active', 'inactive'];
  readonly contractStatusOptions = ['all', 'active', 'cancelled', 'expired', 'missing', 'free'];
  readonly billingConfigLanguageOptions = ['de', 'en'];
  readonly billingFrequencyOptions = ['annual_in_advance'];
  readonly billingActionStatusOptions = ['all', 'pending', 'handled', 'dismissed'];
  readonly billingActionTypeOptions: BillingActionType[] = [
    'property_limit_increased',
    'seat_limit_increased',
    'special_offer_requested',
    'upgrade_contract_signed',
  ];

  constructor(
    private readonly fb: FormBuilder,
    private readonly adminDashboardService: AdminDashboardService,
    private readonly message: NzMessageService,
    private readonly modal: NzModalService,
    private readonly translate: TranslateService,
  ) {
    const now = new Date();
    const from = new Date(now.getTime() - (29 * 24 * 60 * 60 * 1000));

    this.filtersForm = this.fb.group({
      dateRange: [[from, now]],
      plan: ['all'],
      companyStatus: ['all'],
      contractStatus: ['all'],
      excludeTestData: [true],
    });

    this.billingForm = this.fb.group({
      type: ['contract', Validators.required],
      year: [new Date().getFullYear(), Validators.required],
      title: ['', Validators.required],
      documentNumber: [''],
      status: ['active', Validators.required],
      issueDate: [null],
      dueDate: [null],
      contractStartDate: [null],
      contractEndDate: [null],
      amountNet: [this.formatAmountForInput(0), [this.localizedAmountValidator.bind(this)]],
      amountGross: [this.formatAmountForInput(0), [this.localizedAmountValidator.bind(this)]],
      currency: ['EUR'],
      notes: [''],
    });

    this.pricingForm = this.fb.group({
      customUnitPrice: [null, [this.positiveNumberOrEmptyValidator]],
      customUnitPriceValidityUntil: [null],
    });

    this.billingConfigForm = this.fb.group({
      providerLegalName: [''],
      providerAddress: [''],
      providerVatId: [''],
      providerEmail: ['', [Validators.email]],
      providerSignatoryName: [''],
      providerSignatoryRole: [''],
      providerSignaturePlace: [''],
      bankAccountHolder: [''],
      iban: [''],
      bic: [''],
      defaultUnitPriceNetYear: [9.99, [this.positiveNumberValidator]],
      includedUsers: [1, [this.nonNegativeIntegerValidator]],
      pricePerAdditionalUserNetYear: [0, [this.nonNegativeNumberValidator]],
      vatRate: [0.19, [this.vatRateValidator]],
      currency: ['EUR', [this.requiredTrimmedValidator]],
      billingFrequency: ['annual_in_advance', [this.requiredTrimmedValidator]],
      specialOfferThreshold: [100, [this.nonNegativeIntegerValidator]],
      termMonths: [12, [this.positiveIntegerValidator]],
      autoRenewalMonths: [12, [this.positiveIntegerValidator]],
      terminationNoticeMonths: [3, [this.positiveIntegerValidator]],
      priceIncreaseNoticeWeeks: [6, [this.positiveIntegerValidator]],
      paymentTermDays: [14, [this.nonNegativeIntegerValidator]],
      paymentDefaultGraceDays: [30, [this.nonNegativeIntegerValidator]],
      deletionRecoveryDays: [30, [this.positiveIntegerValidator]],
      postContractExportDays: [30, [this.positiveIntegerValidator]],
      confidentialityYearsAfterEnd: [5, [this.positiveIntegerValidator]],
      templateLanguage: ['de', [this.requiredTrimmedValidator]],
      applicableLaw: [''],
      jurisdiction: [''],
      supportEmail: ['contact@scanafy.de', [Validators.email]],
      supportPhone: [''],
      templateVersion: ['v1', [this.requiredTrimmedValidator]],
    });
  }

  ngOnInit(): void {
    this.refreshAllCharts();
    this.loadAll();
    this.loadBillingConfig();

    this.subscriptions.add(
      timer(30_000, 30_000).subscribe(() => {
        this.loadOverview();
        this.loadActivity();
        this.loadBillingActionCount();
      })
    );

    this.subscriptions.add(
      timer(120_000, 120_000).subscribe(() => {
        this.loadCharts();
        this.loadCompanies();
        this.loadContracts();
        if (this.drawerVisible && this.selectedCompany?.companyId) {
          this.loadCompanyDetail(this.selectedCompany.companyId, false);
        }
      })
    );

    this.subscriptions.add(
      this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
        this.refreshAllCharts(event.lang);
        this.reformatBillingAmountInputs();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get filters(): AdminDashboardFilters {
    const range = this.filtersForm.value.dateRange as Date[] | null;
    return {
      from: range?.[0] ? this.toIsoDate(range[0]) : undefined,
      to: range?.[1] ? this.toIsoDate(range[1]) : undefined,
      plan: this.filtersForm.value.plan !== 'all' ? this.filtersForm.value.plan : undefined,
      companyStatus: this.filtersForm.value.companyStatus !== 'all' ? this.filtersForm.value.companyStatus : undefined,
      contractStatus: this.filtersForm.value.contractStatus !== 'all' ? this.filtersForm.value.contractStatus : undefined,
      excludeTestData: !!this.filtersForm.value.excludeTestData,
    };
  }

  get snapshotKpiCards(): Array<{ title: string; value: string | number; sublabel?: string }> {
    return [
      { title: 'ADMIN_DASHBOARD.KPIS.TOTAL_CUSTOMERS', value: this.overview?.totalCustomers ?? '—' },
      { title: 'ADMIN_DASHBOARD.KPIS.PAID_CUSTOMERS', value: this.overview?.paidCustomers ?? '—' },
      { title: 'ADMIN_DASHBOARD.KPIS.ACTIVE_CONTRACTS', value: this.overview?.activeContracts ?? '—' },
      { title: 'ADMIN_DASHBOARD.KPIS.ARR_BOOKED', value: this.formatBillingAmount(this.overview?.arrBookedGross) },
      { title: 'ADMIN_DASHBOARD.KPIS.PROPERTIES', value: this.overview?.totalProperties ?? '—' },
      { title: 'ADMIN_DASHBOARD.KPIS.TOTAL_USERS', value: this.overview?.totalUsers ?? '—' },
    ];
  }

  get periodKpiCards(): Array<{ title: string; value: string | number; sublabel?: string }> {
    return [
      { title: 'ADMIN_DASHBOARD.BILLING_OVERVIEW.PAID_REVENUE_YTD_GROSS', value: this.formatBillingAmount(this.overview?.paidRevenueYTDGross) },
      { title: 'ADMIN_DASHBOARD.BILLING_OVERVIEW.OPEN_INVOICES', value: this.overview?.openInvoicesCount ?? '—' },
      { title: 'ADMIN_DASHBOARD.KPIS.SCANS', value: this.overview?.totalEmergencyScansInRange ?? '—' },
    ];
  }

  get billingOverviewCards(): Array<{ title: string; value: string | number }> {
    return [
      { title: 'ADMIN_DASHBOARD.BILLING_OVERVIEW.ARR_NET', value: this.formatBillingAmount(this.contracts?.arrBookedNet) },
      { title: 'ADMIN_DASHBOARD.BILLING_OVERVIEW.ARR_GROSS', value: this.formatBillingAmount(this.contracts?.arrBookedGross) },
      { title: 'ADMIN_DASHBOARD.BILLING_OVERVIEW.PAID_REVENUE_YTD_NET', value: this.formatBillingAmount(this.contracts?.paidRevenueYTDNet) },
      { title: 'ADMIN_DASHBOARD.BILLING_OVERVIEW.PAID_REVENUE_YTD_GROSS', value: this.formatBillingAmount(this.contracts?.paidRevenueYTDGross) },
      { title: 'ADMIN_DASHBOARD.BILLING_OVERVIEW.INVOICED_YTD_NET', value: this.formatBillingAmount(this.contracts?.invoicedYTDNet) },
      { title: 'ADMIN_DASHBOARD.BILLING_OVERVIEW.INVOICED_YTD_GROSS', value: this.formatBillingAmount(this.contracts?.invoicedYTDGross) },
      { title: 'ADMIN_DASHBOARD.KPIS.ACTIVE_CONTRACTS', value: this.contracts?.activeContracts ?? 0 },
      { title: 'ADMIN_DASHBOARD.BILLING_OVERVIEW.MISSING_CONTRACTS', value: this.contracts?.missingContracts ?? 0 },
      { title: 'ADMIN_DASHBOARD.BILLING_OVERVIEW.OPEN_INVOICES', value: this.contracts?.openInvoicesCount ?? 0 },
      { title: 'ADMIN_DASHBOARD.BILLING_OVERVIEW.OVERDUE_INVOICES', value: this.contracts?.overdueInvoicesCount ?? 0 },
    ];
  }

  get adoptionMiniCards(): Array<{ title: string; value: string | number }> {
    const avgProperties = this.overview?.totalCustomers
      ? ((this.overview?.totalProperties || 0) / this.overview.totalCustomers).toFixed(1)
      : '0.0';
    return [
      { title: 'ADMIN_DASHBOARD.MINI.AVG_PROPERTIES', value: avgProperties },
      { title: 'ADMIN_DASHBOARD.MINI.ZERO_PROPERTIES', value: this.overview?.companiesWithZeroProperties ?? 0 },
      { title: 'ADMIN_DASHBOARD.MINI.NO_SCANS_30D', value: this.overview?.companiesWithNoRecentScans ?? 0 },
      { title: 'ADMIN_DASHBOARD.MINI.STALE_PROPERTIES', value: this.overview?.stalePropertiesCount ?? 0 },
      { title: 'ADMIN_DASHBOARD.MINI.RECENT_UPDATES', value: this.overview?.recentlyUpdatedPropertiesCount ?? 0 },
    ];
  }

  get topRiskCompanies(): AdminDashboardCompanyRow[] {
    return this.companies
      .filter((row) => row.riskFlags.length > 0)
      .slice()
      .sort((a, b) => a.healthScore - b.healthScore)
      .slice(0, 5);
  }

  clearCustomerSearch(): void {
    if (this.customerSearchTimer) {
      clearTimeout(this.customerSearchTimer);
      this.customerSearchTimer = null;
    }
    this.customerSearch = '';
    this.tablePageIndex = 1;
    this.loadCompanies();
  }

  clearBillingSearch(): void {
    if (this.billingSearchTimer) {
      clearTimeout(this.billingSearchTimer);
      this.billingSearchTimer = null;
    }
    this.billingSearch = '';
    this.contractsPageIndex = 1;
    this.loadContracts();
  }

  onCustomerSearchChange(): void {
    if (this.customerSearchTimer) {
      clearTimeout(this.customerSearchTimer);
    }
    this.customerSearchTimer = setTimeout(() => {
      this.tablePageIndex = 1;
      this.loadCompanies();
    }, 300);
  }

  onBillingSearchChange(): void {
    if (this.billingSearchTimer) {
      clearTimeout(this.billingSearchTimer);
    }
    this.billingSearchTimer = setTimeout(() => {
      this.contractsPageIndex = 1;
      this.loadContracts();
    }, 300);
  }

  get selectedPeriodLabel(): string {
    const range = this.filtersForm.value.dateRange as Date[] | null;
    const from = range?.[0] ? new Date(range[0]) : null;
    const to = range?.[1] ? new Date(range[1]) : null;
    if (!from || !to) {
      return this.translate.instant('ADMIN_DASHBOARD.FILTERS.DEFAULT_PERIOD');
    }
    return `${this.formatDateLabel(from)} - ${this.formatDateLabel(to)}`;
  }

  get isSelectedCompanyPaidPlan(): boolean {
    return ['pro', 'business'].includes(String(this.selectedCompany?.plan || '').toLowerCase());
  }

  get billingStatusOptions(): string[] {
    const type = this.billingForm.value.type as BillingDocumentType;
    return type === 'invoice'
      ? ['draft', 'issued', 'paid', 'overdue', 'cancelled']
      : ['active', 'expired', 'cancelled'];
  }

  get isBillingContractType(): boolean {
    return (this.billingForm.value.type as BillingDocumentType) === 'contract';
  }

  get isBillingInvoiceType(): boolean {
    return (this.billingForm.value.type as BillingDocumentType) === 'invoice';
  }

  get billingYearOptions(): number[] {
    const years = new Set<number>();
    this.billingDocuments.forEach((document) => years.add(Number(document.year)));
    if (Number.isFinite(Number(this.billingForm.value.year))) {
      years.add(Number(this.billingForm.value.year));
    }
    return Array.from(years).sort((a, b) => b - a);
  }

  onBillingFiltersChange(): void {
    this.applyBillingDocumentFilters();
  }

  trackBillingDocumentGroup(_: number, group: BillingDocumentGroup): number {
    return Number(group.year);
  }

  trackBillingDocument(_: number, document: BillingDocument): string {
    return document.id;
  }

  private applyBillingDocumentFilters(): void {
    this.filteredBillingDocumentsByYear = (this.billingDocumentsByYear || [])
      .map((group) => ({
        year: group.year,
        documents: (group.documents || []).filter((document) => {
          if (this.selectedBillingYearFilter !== 'all' && Number(document.year) !== Number(this.selectedBillingYearFilter)) {
            return false;
          }
          if (this.selectedBillingTypeFilter !== 'all' && document.type !== this.selectedBillingTypeFilter) {
            return false;
          }
          return true;
        }),
      }))
      .filter((group) => group.documents.length > 0)
      .sort((a, b) => Number(b.year) - Number(a.year))
      .map((group) => ({
        year: group.year,
        documents: [...group.documents].sort((a, b) => this.compareBillingDocuments(a, b)),
      }));
  }

  private compareBillingDocuments(a: BillingDocument, b: BillingDocument): number {
    const issueDateDifference = this.getSortableTimestamp(b.issueDate) - this.getSortableTimestamp(a.issueDate);
    if (issueDateDifference !== 0) {
      return issueDateDifference;
    }

    const createdAtDifference = this.getSortableTimestamp(b.createdAt) - this.getSortableTimestamp(a.createdAt);
    if (createdAtDifference !== 0) {
      return createdAtDifference;
    }

    return (a.title || '').localeCompare(b.title || '');
  }

  private getSortableTimestamp(value: string | null | undefined): number {
    if (!value) {
      return 0;
    }
    const timestamp = new Date(value).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  onApplyFilters(): void {
    this.tablePageIndex = 1;
    this.contractsPageIndex = 1;
    this.activityPageIndex = 1;
    this.loadAll();
  }

  onResetFilters(): void {
    const now = new Date();
    const from = new Date(now.getTime() - (29 * 24 * 60 * 60 * 1000));
    this.filtersForm.reset({
      dateRange: [from, now],
      plan: 'all',
      companyStatus: 'all',
      contractStatus: 'all',
      excludeTestData: true,
    });
    this.tablePageIndex = 1;
    this.contractsPageIndex = 1;
    this.activityPageIndex = 1;
    this.tableSortField = 'healthScore';
    this.tableSortOrder = 'ascend';
    this.contractsSortField = 'latestContractYear';
    this.contractsSortOrder = 'descend';
    this.loadAll();
  }

  onTableQueryParams(params: NzTableQueryParams): void {
    const sort = params.sort.find((entry) => !!entry.value);
    this.tableSortField = sort?.key || this.tableSortField;
    this.tableSortOrder = sort?.value || this.tableSortOrder;
    this.tablePageIndex = params.pageIndex;
    this.tablePageSize = params.pageSize;
    this.loadCompanies();
  }

  onContractsTableQueryParams(params: NzTableQueryParams): void {
    const sort = params.sort.find((entry) => !!entry.value);
    this.contractsSortField = sort?.key || this.contractsSortField;
    this.contractsSortOrder = sort?.value || this.contractsSortOrder;
    this.loadContracts();
  }

  openCompany(row: AdminDashboardCompanyRow): void {
    this.drawerVisible = true;
    this.loadCompanyDetail(row.companyId, true);
  }

  closeDrawer(): void {
    this.drawerVisible = false;
    this.selectedCompany = undefined;
    this.pricingForm.reset({
      customUnitPrice: null,
      customUnitPriceValidityUntil: null,
    });
    this.billingDocuments = [];
    this.billingDocumentsByYear = [];
    this.filteredBillingDocumentsByYear = [];
    this.billingHistory = [];
    this.selectedCompanyTimeline = null;
    this.drawerBillingActions = [];
    this.billingModalVisible = false;
    this.editingBillingDocument = null;
    this.selectedBillingFile = null;
  }

  saveBillingConfig(): void {
    if (this.billingConfigForm.invalid || this.savingBillingConfig) {
      this.billingConfigForm.markAllAsTouched();
      return;
    }

    const formValue = this.billingConfigForm.value;
    const payload: AdminBillingConfig = {
      provider: {
        legalName: formValue.providerLegalName || '',
        address: formValue.providerAddress || '',
        vatId: formValue.providerVatId || '',
        email: formValue.providerEmail || '',
        signatoryName: formValue.providerSignatoryName || '',
        signatoryRole: formValue.providerSignatoryRole || '',
        signaturePlace: formValue.providerSignaturePlace || '',
        bankAccountHolder: formValue.bankAccountHolder || '',
        iban: formValue.iban || '',
        bic: formValue.bic || '',
      },
      pricing: {
        defaultUnitPriceNetYear: Number(formValue.defaultUnitPriceNetYear),
        includedUsers: Number(formValue.includedUsers),
        pricePerAdditionalUserNetYear: Number(formValue.pricePerAdditionalUserNetYear),
        vatRate: Number(formValue.vatRate),
        currency: formValue.currency || 'EUR',
        billingFrequency: formValue.billingFrequency || 'annual_in_advance',
      },
      specialOfferThreshold: formValue.specialOfferThreshold === null || formValue.specialOfferThreshold === ''
        ? null
        : Number(formValue.specialOfferThreshold),
      contractRules: {
        termMonths: Number(formValue.termMonths),
        autoRenewalMonths: Number(formValue.autoRenewalMonths),
        terminationNoticeMonths: Number(formValue.terminationNoticeMonths),
        priceIncreaseNoticeWeeks: Number(formValue.priceIncreaseNoticeWeeks),
        paymentTermDays: Number(formValue.paymentTermDays),
        paymentDefaultGraceDays: Number(formValue.paymentDefaultGraceDays),
        deletionRecoveryDays: Number(formValue.deletionRecoveryDays),
        postContractExportDays: Number(formValue.postContractExportDays),
        confidentialityYearsAfterEnd: Number(formValue.confidentialityYearsAfterEnd),
      },
      template: {
        language: formValue.templateLanguage || 'de',
        applicableLaw: formValue.applicableLaw || '',
        jurisdiction: formValue.jurisdiction || '',
        supportEmail: formValue.supportEmail || 'contact@scanafy.de',
        supportPhone: formValue.supportPhone || '',
        templateVersion: formValue.templateVersion || 'v1',
      },
    };

    this.savingBillingConfig = true;
    this.adminDashboardService.updateBillingConfig(payload)
      .pipe(finalize(() => { this.savingBillingConfig = false; }))
      .subscribe({
        next: (config) => {
          this.billingConfig = config;
          this.patchBillingConfigForm(config);
          this.message.success(this.translate.instant('ADMIN_DASHBOARD.BILLING_CONFIG.SUCCESS_SAVE'));
        },
        error: (error) => {
          this.message.error(error?.message || this.translate.instant('ADMIN_DASHBOARD.BILLING_CONFIG.ERROR_SAVE'));
        },
      });
  }

  onProviderAssetSelected(event: Event, type: 'signatureStamp'): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) {
      return;
    }

    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      this.message.error(this.translate.instant('ADMIN_DASHBOARD.BILLING_CONFIG.ASSETS.INVALID_TYPE'));
      input.value = '';
      return;
    }

    if (file.size > 500 * 1024) {
      this.message.error(this.translate.instant('ADMIN_DASHBOARD.BILLING_CONFIG.ASSETS.INVALID_SIZE'));
      input.value = '';
      return;
    }

    this.setProviderAssetBusy(type, 'upload', true);
    const reader = new FileReader();
    reader.onload = () => {
      const fileBase64 = typeof reader.result === 'string' ? reader.result : '';
      this.adminDashboardService.uploadBillingConfigProviderAsset(type, fileBase64)
        .pipe(finalize(() => {
          this.setProviderAssetBusy(type, 'upload', false);
          input.value = '';
        }))
        .subscribe({
          next: (config) => {
            this.billingConfig = config;
            this.patchBillingConfigForm(config);
            this.loadProviderAssetPreview(type, true);
            this.message.success(this.translate.instant('ADMIN_DASHBOARD.BILLING_CONFIG.ASSETS.SUCCESS_UPLOAD'));
          },
          error: (error) => {
            this.message.error(error?.message || this.translate.instant('ADMIN_DASHBOARD.BILLING_CONFIG.ERROR_SAVE'));
          },
        });
    };
    reader.onerror = () => {
      this.setProviderAssetBusy(type, 'upload', false);
      input.value = '';
      this.message.error(this.translate.instant('ADMIN_DASHBOARD.BILLING_CONFIG.ERROR_SAVE'));
    };
    reader.readAsDataURL(file);
  }

  removeProviderAsset(type: 'signatureStamp'): void {
    this.setProviderAssetBusy(type, 'remove', true);
    this.adminDashboardService.removeBillingConfigProviderAsset(type)
      .pipe(finalize(() => this.setProviderAssetBusy(type, 'remove', false)))
      .subscribe({
        next: (config) => {
          this.billingConfig = config;
          this.patchBillingConfigForm(config);
          this.setProviderAssetPreview(type, '');
          this.message.success(this.translate.instant('ADMIN_DASHBOARD.BILLING_CONFIG.ASSETS.SUCCESS_REMOVE'));
        },
        error: (error) => {
          this.message.error(error?.message || this.translate.instant('ADMIN_DASHBOARD.BILLING_CONFIG.ERROR_SAVE'));
        },
      });
  }

  saveCompanyPricing(): void {
    if (!this.selectedCompany || this.pricingForm.invalid || this.savingPricing) {
      this.pricingForm.markAllAsTouched();
      return;
    }

    const rawCustomUnitPrice = this.pricingForm.value.customUnitPrice;
    const rawValidityUntil = this.pricingForm.value.customUnitPriceValidityUntil;
    const payload = {
      customUnitPrice: rawCustomUnitPrice === null || rawCustomUnitPrice === '' || typeof rawCustomUnitPrice === 'undefined'
        ? null
        : Number(rawCustomUnitPrice),
      customUnitPriceValidityUntil: rawValidityUntil ? this.toIsoDate(rawValidityUntil) : null,
    };

    if (payload.customUnitPrice === null && payload.customUnitPriceValidityUntil) {
      this.message.error(this.translate.instant('ADMIN_DASHBOARD.BILLING.PRICING.VALIDITY_REQUIRES_PRICE'));
      return;
    }

    this.savingPricing = true;
    this.adminDashboardService.updateCompanyPricing(this.selectedCompany.companyId, payload)
      .pipe(finalize(() => { this.savingPricing = false; }))
      .subscribe({
        next: (response) => {
          if (!this.selectedCompany) {
            return;
          }
          this.selectedCompany = {
            ...this.selectedCompany,
            customUnitPrice: response.customUnitPrice,
            customUnitPriceValidityUntil: response.customUnitPriceValidityUntil,
            effectiveUnitPrice: response.effectiveUnitPrice,
          };
          this.patchPricingForm(this.selectedCompany);
          this.loadBillingHistory(this.selectedCompany.companyId, false);
          this.message.success('Company pricing updated');
        },
        error: (error) => {
          this.message.error(error?.message || 'Failed to update company pricing');
        },
      });
  }

  openBillingUploadModal(): void {
    if (!this.selectedCompany) {
      return;
    }
    this.editingBillingDocument = null;
    this.selectedBillingFile = null;
    this.billingForm.reset({
      type: 'contract',
      year: new Date().getFullYear(),
      title: '',
      documentNumber: '',
      status: 'active',
      issueDate: null,
      dueDate: null,
      contractStartDate: null,
      contractEndDate: null,
      amountNet: this.formatAmountForInput(0),
      amountGross: this.formatAmountForInput(0),
      currency: 'EUR',
      notes: '',
    });
    this.billingModalVisible = true;
  }

  openBillingEditModal(document: BillingDocument): void {
    this.editingBillingDocument = document;
    this.selectedBillingFile = null;
    this.billingForm.patchValue({
      type: document.type,
      year: document.year,
      title: document.title,
      documentNumber: document.documentNumber || '',
      status: document.status,
      issueDate: document.issueDate ? new Date(document.issueDate) : null,
      dueDate: document.dueDate ? new Date(document.dueDate) : null,
      contractStartDate: document.contractStartDate ? new Date(document.contractStartDate) : null,
      contractEndDate: document.contractEndDate ? new Date(document.contractEndDate) : null,
      amountNet: this.formatAmountForInput(document.amountNet || 0),
      amountGross: this.formatAmountForInput(document.amountGross || 0),
      currency: document.currency || 'EUR',
      notes: document.notes || '',
    });
    this.billingModalVisible = true;
  }

  closeBillingModal(): void {
    this.billingModalVisible = false;
    this.editingBillingDocument = null;
    this.selectedBillingFile = null;
  }

  onBillingFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    this.selectedBillingFile = file;
  }

  saveBillingDocument(): void {
    if (!this.selectedCompany || this.billingForm.invalid || this.savingBillingDocument) {
      this.billingForm.markAllAsTouched();
      return;
    }

    const raw = this.billingForm.value;
    const isContract = raw.type === 'contract';
    let parsedAmountNet = 0;
    let parsedAmountGross = 0;

    try {
      parsedAmountNet = this.parseLocalizedAmount(raw.amountNet);
      parsedAmountGross = this.parseLocalizedAmount(raw.amountGross);
    } catch {
      this.billingForm.controls['amountNet'].updateValueAndValidity();
      this.billingForm.controls['amountGross'].updateValueAndValidity();
      this.billingForm.markAllAsTouched();
      this.message.error(this.translate.instant('ADMIN_DASHBOARD.BILLING.INVALID_AMOUNT'));
      return;
    }

    const payload: UpdateBillingDocumentPayload = {
      type: raw.type,
      year: Number(raw.year),
      title: String(raw.title || '').trim(),
      documentNumber: String(raw.documentNumber || '').trim(),
      status: raw.status,
      issueDate: isContract ? null : (raw.issueDate ? this.toIsoDate(raw.issueDate) : null),
      dueDate: isContract ? null : (raw.dueDate ? this.toIsoDate(raw.dueDate) : null),
      contractStartDate: isContract ? (raw.contractStartDate ? this.toIsoDate(raw.contractStartDate) : null) : null,
      contractEndDate: null,
      amountNet: parsedAmountNet,
      amountGross: parsedAmountGross,
      currency: String(raw.currency || 'EUR').trim().toUpperCase(),
      notes: String(raw.notes || ''),
    };

    this.savingBillingDocument = true;

    if (this.editingBillingDocument) {
      this.adminDashboardService.updateBillingDocument(this.editingBillingDocument.id, payload)
        .pipe(finalize(() => { this.savingBillingDocument = false; }))
        .subscribe({
          next: () => {
            this.message.success(this.translate.instant('ADMIN_DASHBOARD.BILLING.SAVED'));
            this.closeBillingModal();
            this.loadAll();
            this.loadCompanyDetail(this.selectedCompany!.companyId, false);
            this.loadBillingDocuments(this.selectedCompany!.companyId, true);
          },
          error: (error) => {
            this.message.error(error?.message || this.translate.instant('ADMIN_DASHBOARD.BILLING.ERROR_SAVE'));
          },
        });
      return;
    }

    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== null && typeof value !== 'undefined') {
        formData.append(key, String(value));
      }
    });
    if (this.selectedBillingFile) {
      formData.append('file', this.selectedBillingFile);
    }

    this.adminDashboardService.createCompanyBillingDocument(this.selectedCompany.companyId, formData)
      .pipe(finalize(() => { this.savingBillingDocument = false; }))
      .subscribe({
        next: () => {
          this.message.success(this.translate.instant('ADMIN_DASHBOARD.BILLING.UPLOADED'));
          this.closeBillingModal();
          this.loadAll();
          this.loadCompanyDetail(this.selectedCompany!.companyId, false);
          this.loadBillingDocuments(this.selectedCompany!.companyId, true);
        },
        error: (error) => {
          this.message.error(error?.message || this.translate.instant('ADMIN_DASHBOARD.BILLING.ERROR_UPLOAD'));
        },
      });
  }

  archiveBillingDocument(document: BillingDocument): void {
    if (!window.confirm(this.translate.instant('ADMIN_DASHBOARD.BILLING.CONFIRM_ARCHIVE'))) {
      return;
    }
    this.adminDashboardService.archiveBillingDocument(document.id).subscribe({
      next: () => {
        this.message.success(this.translate.instant('ADMIN_DASHBOARD.BILLING.ARCHIVED'));
        if (this.selectedCompany) {
          this.loadAll();
          this.loadCompanyDetail(this.selectedCompany.companyId, false);
          this.loadBillingDocuments(this.selectedCompany.companyId, true);
        }
      },
      error: (error) => {
        this.message.error(error?.message || this.translate.instant('ADMIN_DASHBOARD.BILLING.ERROR_ARCHIVE'));
      },
    });
  }

  downloadBillingDocument(document: BillingDocument): void {
    if (document.migratedWithoutFile || !document.hasFile) {
      return;
    }
    this.adminDashboardService.getAdminBillingDocumentDownloadUrl(document.id).subscribe({
      next: ({ url }) => window.open(url, '_blank', 'noopener'),
      error: (error) => {
        this.message.error(error?.message || this.translate.instant('ADMIN_DASHBOARD.BILLING.ERROR_LOAD'));
      },
    });
  }

  planLabel(plan: string): string {
    return this.translate.instant(`PLAN.${String(plan || 'free').toUpperCase()}`);
  }

  contractStatusColor(status: string): string {
    switch (status) {
      case 'active':
        return 'green';
      case 'cancelled':
        return 'default';
      case 'expired':
        return 'red';
      case 'missing':
        return 'gold';
      default:
        return 'blue';
    }
  }

  billingDocumentStatusColor(status: string): string {
    switch (status) {
      case 'active':
      case 'paid':
        return 'green';
      case 'issued':
        return 'blue';
      case 'draft':
        return 'gold';
      case 'expired':
      case 'overdue':
        return 'red';
      case 'cancelled':
      default:
        return 'default';
    }
  }

  healthColor(score: number): string {
    if (score >= 75) return '#1e9f6e';
    if (score >= 45) return '#f59e0b';
    return '#dc2626';
  }

  activityIcon(type: string): string {
    switch (type) {
      case 'company_created': return 'bank';
      case 'billing_document': return 'file-text';
      case 'property_created': return 'home';
      case 'property_updated': return 'edit';
      case 'support_request_created': return 'customer-service';
      case 'session_activity': return 'login';
      default: return 'clock-circle';
    }
  }

  formatSeconds(seconds?: number | null): string {
    const totalSeconds = Math.max(0, Number(seconds || 0));
    if (!totalSeconds) {
      return '0m';
    }
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  }

  formatCurrency(value?: number | null, currency = 'EUR'): string {
    return new Intl.NumberFormat(this.translate.currentLang || 'en', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value || 0));
  }

  formatBillingAmount(value?: number | null, currency = 'EUR'): string {
    const locale = (this.translate.currentLang || 'en').toLowerCase().startsWith('de') ? 'de-DE' : 'en';
    return `${new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value || 0))} ${currency}`;
  }

  formatBillingDate(value?: string | Date | null): string {
    if (!value) {
      return '—';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }

    const isGerman = (this.translate.currentLang || '').toLowerCase().startsWith('de');
    return new Intl.DateTimeFormat(isGerman ? 'de-DE' : 'en', {
      day: isGerman ? '2-digit' : 'numeric',
      month: isGerman ? '2-digit' : 'short',
      year: 'numeric',
    }).format(date);
  }

  formatAmountForInput(value: number | null | undefined): string {
    const locale = (this.translate.currentLang || 'en').toLowerCase().startsWith('de') ? 'de-DE' : 'en';
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value || 0));
  }

  private localizedAmountValidator(control: AbstractControl): ValidationErrors | null {
    try {
      this.parseLocalizedAmount(control.value);
      return null;
    } catch {
      return { invalidAmount: true };
    }
  }

  private reformatBillingAmountInputs(): void {
    if (!this.billingModalVisible) {
      return;
    }

    const amountFields: Array<'amountNet' | 'amountGross'> = ['amountNet', 'amountGross'];
    amountFields.forEach((field) => {
      const control = this.billingForm.get(field);
      if (!control) {
        return;
      }
      try {
        const parsed = this.parseLocalizedAmount(control.value);
        control.setValue(this.formatAmountForInput(parsed), { emitEvent: false });
      } catch {
        // Leave the current user input untouched if it cannot be parsed.
      }
    });
  }

  private parseLocalizedAmount(value: unknown): number {
    const raw = String(value ?? '').trim();
    if (!raw) {
      return 0;
    }

    const compact = raw.replace(/\s/g, '');
    if (!/^[\d.,]+$/.test(compact)) {
      throw new Error('Invalid amount');
    }

    const normalized = this.normalizeAmountString(compact);
    if (!/^\d+(\.\d{1,2})?$/.test(normalized)) {
      throw new Error('Invalid amount');
    }

    const parsed = Number(normalized);
    if (!Number.isFinite(parsed) || parsed < 0) {
      throw new Error('Invalid amount');
    }

    return Math.round(parsed * 100) / 100;
  }

  private normalizeAmountString(raw: string): string {
    const lastDot = raw.lastIndexOf('.');
    const lastComma = raw.lastIndexOf(',');

    if (lastDot !== -1 && lastComma !== -1) {
      const decimalSeparator = lastDot > lastComma ? '.' : ',';
      const decimalIndex = Math.max(lastDot, lastComma);
      const integerPart = raw.slice(0, decimalIndex).replace(/[.,]/g, '');
      const fractionPart = raw.slice(decimalIndex + 1).replace(/[.,]/g, '');
      return fractionPart ? `${integerPart}.${fractionPart}` : integerPart;
    }

    if (lastComma !== -1) {
      const parts = raw.split(',');
      if (parts.length === 2 && parts[1].length <= 2) {
        return `${parts[0].replace(/,/g, '').replace(/\./g, '')}.${parts[1]}`;
      }
      return raw.replace(/,/g, '');
    }

    if (lastDot !== -1) {
      const parts = raw.split('.');
      if (parts.length === 2 && parts[1].length <= 2) {
        return raw.replace(/,/g, '');
      }
      if (parts.length > 2 && parts[parts.length - 1].length <= 2) {
        const fractionPart = parts.pop() || '';
        return `${parts.join('')}.${fractionPart}`;
      }
      return raw.replace(/\./g, '');
    }

    return raw;
  }

  onContractsPageChange(pageIndex: number): void {
    this.contractsPageIndex = pageIndex;
    this.loadContracts();
  }

  onContractsPageSizeChange(pageSize: number): void {
    this.contractsPageSize = pageSize;
    this.contractsPageIndex = 1;
    this.loadContracts();
  }

  onActivityPageChange(pageIndex: number): void {
    this.activityPageIndex = pageIndex;
    this.loadActivity();
  }

  private loadAll(): void {
    this.errorMessage = '';
    this.loadOverview();
    this.loadCharts();
    this.loadCompanies();
    this.loadContracts();
    this.loadActivity();
    this.loadBillingActions();
    this.loadBillingActionCount();
    this.loadContractAlerts();
  }

  private loadOverview(): void {
    const requestId = ++this.overviewRequestId;
    this.loadingOverview = true;
    this.adminDashboardService.getOverview(this.filters)
      .pipe(finalize(() => {
        if (requestId === this.overviewRequestId) {
          this.loadingOverview = false;
        }
      }))
      .subscribe({
        next: (overview) => {
          if (requestId !== this.overviewRequestId) {
            return;
          }
          this.overview = overview;
        },
        error: (error) => {
          if (requestId !== this.overviewRequestId) {
            return;
          }
          this.handleError(error);
        },
      });
  }

  private loadCharts(): void {
    const requestId = ++this.chartsRequestId;
    this.loadingCharts = true;
    this.adminDashboardService.getCharts(this.filters)
      .pipe(finalize(() => {
        if (requestId === this.chartsRequestId) {
          this.loadingCharts = false;
        }
      }))
      .subscribe({
        next: (charts) => {
          if (requestId !== this.chartsRequestId) {
            return;
          }
          this.charts = charts;
          this.refreshOverviewCharts();
        },
        error: (error) => {
          if (requestId !== this.chartsRequestId) {
            return;
          }
          this.handleError(error);
        },
      });
  }

  private loadCompanies(): void {
    const requestId = ++this.companiesRequestId;
    this.loadingCompanies = true;
    this.adminDashboardService.getCompanies({
      ...this.filters,
      page: this.tablePageIndex,
      pageSize: this.tablePageSize,
      sortField: this.tableSortField,
      sortOrder: this.tableSortOrder,
      search: this.customerSearch || undefined,
    })
      .pipe(finalize(() => {
        if (requestId === this.companiesRequestId) {
          this.loadingCompanies = false;
        }
      }))
      .subscribe({
        next: (response: AdminDashboardCompanyResponse) => {
          if (requestId !== this.companiesRequestId) {
            return;
          }
          this.companies = response.items || [];
          this.tableTotal = response.total || 0;
          this.tablePageIndex = response.page || this.tablePageIndex;
          this.tablePageSize = response.pageSize || this.tablePageSize;
        },
        error: (error) => {
          if (requestId !== this.companiesRequestId) {
            return;
          }
          this.handleError(error);
        },
      });
  }

  private loadContracts(): void {
    const requestId = ++this.contractsRequestId;
    this.loadingContracts = true;
    this.adminDashboardService.getContracts({
      ...this.filters,
      page: this.contractsPageIndex,
      pageSize: this.contractsPageSize,
      sortField: this.contractsSortField,
      sortOrder: this.contractsSortOrder,
      search: this.billingSearch || undefined,
    })
      .pipe(finalize(() => {
        if (requestId === this.contractsRequestId) {
          this.loadingContracts = false;
        }
      }))
      .subscribe({
        next: (contracts) => {
          if (requestId !== this.contractsRequestId) {
            return;
          }
          this.contracts = contracts;
          this.contractsPageIndex = contracts.page || this.contractsPageIndex;
          this.contractsPageSize = contracts.pageSize || this.contractsPageSize;
        },
        error: (error) => {
          if (requestId !== this.contractsRequestId) {
            return;
          }
          this.handleError(error);
        },
      });
  }

  private loadActivity(): void {
    const requestId = ++this.activityRequestId;
    this.loadingActivity = true;
    this.adminDashboardService.getActivity({
      ...this.filters,
      page: this.activityPageIndex,
      pageSize: this.activityPageSize,
      limit: this.activityPageSize,
    })
      .pipe(finalize(() => {
        if (requestId === this.activityRequestId) {
          this.loadingActivity = false;
        }
      }))
      .subscribe({
        next: (activity: AdminDashboardActivityResponse) => {
          if (requestId !== this.activityRequestId) {
            return;
          }
          this.activity = activity?.items || [];
          this.activityTotal = activity?.total || 0;
          this.activityPageIndex = activity?.page || this.activityPageIndex;
          this.activityPageSize = activity?.pageSize || this.activityPageSize;
        },
        error: (error) => {
          if (requestId !== this.activityRequestId) {
            return;
          }
          this.handleError(error);
        },
      });
  }

  private loadCompanyDetail(companyId: string, withFormReset: boolean): void {
    const requestId = ++this.companyDetailRequestId;
    this.loadingCompanyDetail = true;
    this.adminDashboardService.getCompanyDetail(companyId, this.filters)
      .pipe(finalize(() => {
        if (requestId === this.companyDetailRequestId) {
          this.loadingCompanyDetail = false;
        }
      }))
      .subscribe({
        next: (company) => {
          if (requestId !== this.companyDetailRequestId) {
            return;
          }
          this.selectedCompany = company;
          if (withFormReset || !this.pricingForm.dirty) {
            this.patchPricingForm(company);
          }
          this.refreshDetailCharts();
          this.loadCompanyContractTimeline(company.companyId, false);
          this.loadBillingDocuments(company.companyId, false);
          this.loadBillingHistory(company.companyId, false);
          this.loadDrawerBillingActions(company.companyId);
        },
        error: (error) => {
          if (requestId !== this.companyDetailRequestId) {
            return;
          }
          this.handleError(error);
          this.drawerVisible = false;
        },
      });
  }

  private loadCompanyContractTimeline(companyId: string, showErrors = true): void {
    this.loadingContractTimeline = true;
    this.adminDashboardService.getCompanyContractTimeline(companyId)
      .pipe(finalize(() => { this.loadingContractTimeline = false; }))
      .subscribe({
        next: (timeline) => {
          this.selectedCompanyTimeline = timeline;
        },
        error: (error) => {
          this.selectedCompanyTimeline = null;
          if (showErrors) {
            this.message.error(error?.message || this.translate.instant('ADMIN_DASHBOARD.CONTRACT_TIMELINE.NO_DATA'));
          }
        },
      });
  }

  loadBillingDocuments(companyId: string, showErrors = true): void {
    this.loadingBillingDocuments = true;
    this.adminDashboardService.getCompanyBillingDocuments(companyId, {
      year: this.selectedBillingYearFilter === 'all' ? null : this.selectedBillingYearFilter,
      type: this.selectedBillingTypeFilter === 'all' ? null : this.selectedBillingTypeFilter,
    })
      .pipe(finalize(() => { this.loadingBillingDocuments = false; }))
      .subscribe({
        next: (response) => {
          this.billingDocuments = response.documents || [];
          this.billingDocumentsByYear = response.documentsByYear || [];
          this.applyBillingDocumentFilters();
        },
        error: (error) => {
          this.billingDocuments = [];
          this.billingDocumentsByYear = [];
          this.filteredBillingDocumentsByYear = [];
          if (showErrors) {
            this.message.error(error?.message || this.translate.instant('ADMIN_DASHBOARD.BILLING.ERROR_LOAD'));
          }
        },
      });
  }

  loadBillingHistory(companyId: string, showErrors = true): void {
    this.loadingBillingHistory = true;
    this.adminDashboardService.getCompanyBillingHistory(companyId)
      .pipe(finalize(() => { this.loadingBillingHistory = false; }))
      .subscribe({
        next: (history) => {
          this.billingHistory = history || [];
        },
        error: (error) => {
          this.billingHistory = [];
          if (showErrors) {
            this.message.error(error?.message || this.translate.instant('ADMIN_DASHBOARD.BILLING.ERROR_LOAD'));
          }
        },
      });
  }

  getHistoryChangeEntries(changes: any): Array<{ key: string; from: any; to: any }> {
    const before = changes?.before || {};
    const after = changes?.after || {};
    const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));
    return keys
      .filter((key) => JSON.stringify(before[key]) !== JSON.stringify(after[key]))
      .map((key) => ({ key, from: before[key], to: after[key] }));
  }

  formatHistoryFieldLabel(key: string): string {
    if (key === 'customUnitPrice') {
      return 'Custom unit price';
    }
    if (key === 'customUnitPriceValidityUntil') {
      return 'Validity until';
    }
    return key;
  }

  formatHistoryValue(value: any): string {
    if (value === null || typeof value === 'undefined' || value === '') {
      return '—';
    }
    if (typeof value === 'number') {
      return `${value} EUR`;
    }
    if (value instanceof Date) {
      return this.formatDateLabel(value);
    }
    if (typeof value === 'string') {
      const parsedDate = new Date(value);
      if (!Number.isNaN(parsedDate.getTime()) && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        return this.formatDateLabel(parsedDate);
      }
    }
    return String(value);
  }

  private handleError(error: any): void {
    this.errorMessage = error?.message || this.translate.instant('ADMIN_DASHBOARD.ERRORS.LOAD');
  }

  private refreshAllCharts(lang = this.translate.currentLang): void {
    void lang;
    this.refreshOverviewCharts();
    this.refreshDetailCharts();
  }

  private refreshOverviewCharts(): void {
    this.customerGrowthChartData = {
      labels: (this.charts?.customerGrowth || []).map((entry) => entry.date),
      datasets: [
        {
          label: this.translate.instant('ADMIN_DASHBOARD.CHARTS.CUSTOMER_GROWTH'),
          data: (this.charts?.customerGrowth || []).map((entry) => entry.cumulative || entry.count || 0),
          borderColor: '#0b6bcb',
          backgroundColor: 'rgba(11,107,203,0.18)',
          tension: 0.3,
          fill: true,
        },
        {
          label: this.translate.instant('ADMIN_DASHBOARD.CHARTS.PAID_CUSTOMER_GROWTH'),
          data: (this.charts?.paidCustomerGrowth || []).map((entry) => entry.cumulative || entry.count || 0),
          borderColor: '#1e9f6e',
          backgroundColor: 'rgba(30,159,110,0.12)',
          tension: 0.3,
          fill: false,
        },
      ],
    };

    this.arrTrendChartData = {
      labels: (this.charts?.contractValueTrend || []).map((entry) => entry.date),
      datasets: [
        {
          label: this.translate.instant('ADMIN_DASHBOARD.CHARTS.ARR_TREND'),
          data: (this.charts?.contractValueTrend || []).map((entry) => entry.value),
          backgroundColor: '#f59e0b',
          borderRadius: 6,
          borderSkipped: false,
        },
      ],
    };

    this.propertyTrendChartData = {
      labels: (this.charts?.propertyOnboardingTrend || []).map((entry) => entry.date),
      datasets: [
        {
          label: this.translate.instant('ADMIN_DASHBOARD.CHARTS.PROPERTY_ONBOARDING'),
          data: (this.charts?.propertyOnboardingTrend || []).map((entry) => entry.count || 0),
          borderColor: '#6d28d9',
          backgroundColor: 'rgba(109,40,217,0.15)',
          fill: true,
          tension: 0.3,
        },
      ],
    };

    this.planMixChartData = {
      labels: (this.charts?.planMix || []).map((entry) => this.translate.instant(`PLAN.${entry.plan.toUpperCase()}`)),
      datasets: [
        {
          data: (this.charts?.planMix || []).map((entry) => entry.count),
          backgroundColor: ['#94a3b8', '#0b6bcb', '#1e9f6e'],
          hoverOffset: 4,
        },
      ],
    };

    this.workforceChartData = {
      labels: (this.charts?.workforceCompositionByPlan || []).map((entry) => this.translate.instant(`PLAN.${entry.plan.toUpperCase()}`)),
      datasets: [
        {
          label: this.translate.instant('ADMIN_DASHBOARD.WORKFORCE.SERVICES'),
          data: (this.charts?.workforceCompositionByPlan || []).map((entry) => entry.technicalServiceCompaniesCount),
          backgroundColor: '#0b6bcb',
        },
        {
          label: this.translate.instant('ADMIN_DASHBOARD.WORKFORCE.CARETAKERS'),
          data: (this.charts?.workforceCompositionByPlan || []).map((entry) => entry.caretakersCount),
          backgroundColor: '#f59e0b',
        },
        {
          label: this.translate.instant('ADMIN_DASHBOARD.WORKFORCE.PROPERTY_MANAGERS'),
          data: (this.charts?.workforceCompositionByPlan || []).map((entry) => entry.propertyManagersCount),
          backgroundColor: '#1e9f6e',
        },
        {
          label: this.translate.instant('ADMIN_DASHBOARD.WORKFORCE.TECH_ASSET_MANAGERS'),
          data: (this.charts?.workforceCompositionByPlan || []).map((entry) => entry.technicalAssetManagersCount),
          backgroundColor: '#6d28d9',
        },
      ],
    };
  }

  private refreshDetailCharts(): void {
    this.detailScansChartData = {
      labels: (this.selectedCompany?.trends.scans || []).map((entry) => entry.date),
      datasets: [
        {
          label: this.translate.instant('ADMIN_DASHBOARD.DRAWER.SCAN_TREND'),
          data: (this.selectedCompany?.trends.scans || []).map((entry) => entry.count),
          borderColor: '#0b6bcb',
          backgroundColor: 'rgba(11,107,203,0.14)',
          fill: true,
          tension: 0.3,
        },
      ],
    };

    this.detailTimeChartData = {
      labels: (this.selectedCompany?.trends.timeSpent || []).map((entry) => entry.date),
      datasets: [
        {
          label: this.translate.instant('ADMIN_DASHBOARD.DRAWER.TIME_TREND'),
          data: (this.selectedCompany?.trends.timeSpent || []).map((entry) => Math.round(entry.totalTimeSpent / 60)),
          borderColor: '#1e9f6e',
          backgroundColor: 'rgba(30,159,110,0.14)',
          fill: true,
          tension: 0.3,
        },
      ],
    };
  }

  private buildCartesianChartOptions<TType extends 'line' | 'bar'>(): ChartOptions<TType> {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      resizeDelay: 150,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {
          display: true,
          labels: {
            usePointStyle: true,
            boxWidth: 10,
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            maxRotation: 0,
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(148, 163, 184, 0.18)',
          },
        },
      },
    } as unknown as ChartOptions<TType>;
  }

  private buildStackedBarChartOptions(): ChartOptions<'bar'> {
    return {
      ...this.buildCartesianChartOptions(),
      scales: {
        x: { stacked: true, grid: { display: false } },
        y: { stacked: true, beginAtZero: true, grid: { color: 'rgba(148, 163, 184, 0.18)' } },
      },
    };
  }

  private buildDoughnutChartOptions(): ChartOptions<'doughnut'> {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      resizeDelay: 150,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            boxWidth: 10,
          },
        },
      },
    };
  }

  private patchPricingForm(company: AdminDashboardCompanyDetail): void {
    this.pricingForm.reset({
      customUnitPrice: company.customUnitPrice ?? null,
      customUnitPriceValidityUntil: company.customUnitPriceValidityUntil ? new Date(company.customUnitPriceValidityUntil) : null,
    });
  }

  private loadBillingConfig(): void {
    this.loadingBillingConfig = true;
    this.adminDashboardService.getBillingConfig()
      .pipe(finalize(() => { this.loadingBillingConfig = false; }))
      .subscribe({
        next: (config) => {
          this.billingConfig = config;
          this.patchBillingConfigForm(config);
          this.refreshProviderAssetPreviews(config);
        },
        error: (error) => {
          this.message.error(error?.message || this.translate.instant('ADMIN_DASHBOARD.BILLING_CONFIG.ERROR_LOAD'));
        },
      });
  }

  private patchBillingConfigForm(config: AdminBillingConfig): void {
    this.billingConfigForm.reset({
      providerLegalName: config.provider?.legalName || '',
      providerAddress: config.provider?.address || '',
      providerVatId: config.provider?.vatId || '',
      providerEmail: config.provider?.email || '',
      providerSignatoryName: config.provider?.signatoryName || '',
      providerSignatoryRole: config.provider?.signatoryRole || '',
      providerSignaturePlace: config.provider?.signaturePlace || '',
      bankAccountHolder: config.provider?.bankAccountHolder || '',
      iban: config.provider?.iban || '',
      bic: config.provider?.bic || '',
      defaultUnitPriceNetYear: config.pricing?.defaultUnitPriceNetYear ?? 9.99,
      includedUsers: config.pricing?.includedUsers ?? 1,
      pricePerAdditionalUserNetYear: config.pricing?.pricePerAdditionalUserNetYear ?? 0,
      vatRate: config.pricing?.vatRate ?? 0.19,
      currency: config.pricing?.currency || 'EUR',
      billingFrequency: config.pricing?.billingFrequency || 'annual_in_advance',
      specialOfferThreshold: config.specialOfferThreshold ?? 100,
      termMonths: config.contractRules?.termMonths ?? 12,
      autoRenewalMonths: config.contractRules?.autoRenewalMonths ?? 12,
      terminationNoticeMonths: config.contractRules?.terminationNoticeMonths ?? 3,
      priceIncreaseNoticeWeeks: config.contractRules?.priceIncreaseNoticeWeeks ?? 6,
      paymentTermDays: config.contractRules?.paymentTermDays ?? 14,
      paymentDefaultGraceDays: config.contractRules?.paymentDefaultGraceDays ?? 30,
      deletionRecoveryDays: config.contractRules?.deletionRecoveryDays ?? 30,
      postContractExportDays: config.contractRules?.postContractExportDays ?? 30,
      confidentialityYearsAfterEnd: config.contractRules?.confidentialityYearsAfterEnd ?? 5,
      templateLanguage: config.template?.language || 'de',
      applicableLaw: config.template?.applicableLaw || '',
      jurisdiction: config.template?.jurisdiction || '',
      supportEmail: config.template?.supportEmail || 'contact@scanafy.de',
      supportPhone: config.template?.supportPhone || '',
      templateVersion: config.template?.templateVersion || 'v1',
    });
  }

  private refreshProviderAssetPreviews(config: AdminBillingConfig): void {
    if (config.provider?.signatureStampImageConfigured) {
      this.loadProviderAssetPreview('signatureStamp');
    } else {
      this.providerSignatureStampPreviewUrl = '';
    }
  }

  private loadProviderAssetPreview(type: 'signatureStamp', force = false): void {
    const current = this.providerSignatureStampPreviewUrl;
    if (current && !force) {
      return;
    }

    this.adminDashboardService.getBillingConfigProviderAssetPreviewUrl(type).subscribe({
      next: ({ url }) => this.setProviderAssetPreview(type, url || ''),
      error: () => this.setProviderAssetPreview(type, ''),
    });
  }

  private setProviderAssetPreview(_type: 'signatureStamp', url: string): void {
    this.providerSignatureStampPreviewUrl = url;
  }

  private setProviderAssetBusy(_type: 'signatureStamp', action: 'upload' | 'remove', value: boolean): void {
    if (action === 'upload') {
      this.uploadingProviderSignatureStamp = value;
    } else {
      this.removingProviderSignatureStamp = value;
    }
  }

  private positiveNumberOrEmptyValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value === null || value === '' || typeof value === 'undefined') {
      return null;
    }

    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      return { positiveNumber: true };
    }

    return null;
  }

  billingConfigErrorTip(controlName: string): string | null {
    const control = this.billingConfigForm.get(controlName);
    if (!control || (!control.touched && !control.dirty)) {
      return null;
    }
    if (control.hasError('required')) {
      return this.translate.instant('ADMIN_DASHBOARD.BILLING_CONFIG.VALIDATION.REQUIRED');
    }
    if (control.hasError('email')) {
      return this.translate.instant('ADMIN_DASHBOARD.BILLING_CONFIG.VALIDATION.EMAIL');
    }
    if (control.hasError('positiveNumber')) {
      return this.translate.instant('ADMIN_DASHBOARD.BILLING_CONFIG.VALIDATION.POSITIVE_NUMBER');
    }
    if (control.hasError('nonNegativeNumber')) {
      return this.translate.instant('ADMIN_DASHBOARD.BILLING_CONFIG.VALIDATION.NON_NEGATIVE_NUMBER');
    }
    if (control.hasError('positiveInteger')) {
      return this.translate.instant('ADMIN_DASHBOARD.BILLING_CONFIG.VALIDATION.POSITIVE_INTEGER');
    }
    if (control.hasError('nonNegativeInteger')) {
      return this.translate.instant('ADMIN_DASHBOARD.BILLING_CONFIG.VALIDATION.NON_NEGATIVE_INTEGER');
    }
    if (control.hasError('vatRate')) {
      return this.translate.instant('ADMIN_DASHBOARD.BILLING_CONFIG.VALIDATION.VAT_RATE');
    }
    return null;
  }

  private requiredTrimmedValidator(control: AbstractControl): ValidationErrors | null {
    const value = typeof control.value === 'string' ? control.value.trim() : control.value;
    return value ? null : { required: true };
  }

  private positiveNumberValidator(control: AbstractControl): ValidationErrors | null {
    const numeric = Number(control.value);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      return { positiveNumber: true };
    }
    return null;
  }

  private nonNegativeNumberValidator(control: AbstractControl): ValidationErrors | null {
    const numeric = Number(control.value);
    if (!Number.isFinite(numeric) || numeric < 0) {
      return { nonNegativeNumber: true };
    }
    return null;
  }

  private positiveIntegerValidator(control: AbstractControl): ValidationErrors | null {
    const numeric = Number(control.value);
    if (!Number.isInteger(numeric) || numeric <= 0) {
      return { positiveInteger: true };
    }
    return null;
  }

  private nonNegativeIntegerValidator(control: AbstractControl): ValidationErrors | null {
    const numeric = Number(control.value);
    if (!Number.isInteger(numeric) || numeric < 0) {
      return { nonNegativeInteger: true };
    }
    return null;
  }

  private vatRateValidator(control: AbstractControl): ValidationErrors | null {
    const numeric = Number(control.value);
    if (!Number.isFinite(numeric) || numeric < 0 || numeric > 1) {
      return { vatRate: true };
    }
    return null;
  }

  private toIsoDate(value: Date): string {
    const date = new Date(value);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  private formatDateLabel(value: Date): string {
    return new Intl.DateTimeFormat(this.translate.currentLang || 'en', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(value);
  }

  private loadDrawerBillingActions(companyId: string): void {
    this.loadingDrawerBillingActions = true;
    this.adminDashboardService.listBillingActions({
      companyId,
      pageSize: 5,
    }).subscribe({
      next: (response) => {
        this.drawerBillingActions = response.items;
        this.loadingDrawerBillingActions = false;
      },
      error: () => {
        this.drawerBillingActions = [];
        this.loadingDrawerBillingActions = false;
      },
    });
  }

  loadBillingActions(): void {
    this.loadingBillingActions = true;
    const requestId = ++this.billingActionRequestId;

    this.adminDashboardService.listBillingActions({
      status: this.billingActionStatusFilter,
      type: this.billingActionTypeFilter,
      search: this.billingActionSearch || undefined,
      page: this.billingActionsPage,
      pageSize: this.billingActionsPageSize,
    }).subscribe({
      next: (response) => {
        if (requestId !== this.billingActionRequestId) { return; }
        this.billingActions = response.items;
        this.billingActionsTotal = response.total;
        this.billingActionsPage = response.page;
        this.billingActionsHasMore = response.hasMore;
        this.loadingBillingActions = false;
      },
      error: () => {
        this.loadingBillingActions = false;
        this.message.error(this.translate.instant('BILLING_ACTIONS.ERRORS.LOAD'));
      },
    });
  }

  loadBillingActionCount(): void {
    this.adminDashboardService.countBillingActions().subscribe({
      next: (response) => {
        this.billingActionCount = response;
      },
    });
  }

  loadContractAlerts(): void {
    this.loadingContractAlerts = true;
    this.adminDashboardService.getContractAlerts()
      .pipe(finalize(() => { this.loadingContractAlerts = false; }))
      .subscribe({
        next: (response) => {
          this.contractAlerts = response.items || [];
        },
        error: () => {
          this.contractAlerts = [];
          this.message.error(this.translate.instant('ADMIN_DASHBOARD.ERRORS.LOAD'));
        },
      });
  }

  onBillingActionStatusFilterChange(status: string): void {
    this.billingActionStatusFilter = status;
    this.billingActionsPage = 1;
    this.loadBillingActions();
  }

  onBillingActionTypeFilterChange(type: string): void {
    this.billingActionTypeFilter = type;
    this.billingActionsPage = 1;
    this.loadBillingActions();
  }

  onBillingActionSearchChange(): void {
    this.billingActionsPage = 1;
    this.loadBillingActions();
  }

  onBillingActionPageChange(page: number): void {
    this.billingActionsPage = page;
    this.loadBillingActions();
  }

  handleBillingAction(item: BillingActionItem): void {
    this.modal.confirm({
      nzTitle: this.translate.instant('BILLING_ACTIONS.CONFIRM.HANDLE_TITLE'),
      nzContent: this.translate.instant('BILLING_ACTIONS.CONFIRM.HANDLE_CONTENT'),
      nzOkText: this.translate.instant('BILLING_ACTIONS.ACTIONS.MARK_HANDLED'),
      nzOnOk: () => {
        this.adminDashboardService.markBillingActionHandled(item._id).subscribe({
          next: () => {
            this.message.success(this.translate.instant('BILLING_ACTIONS.SUCCESS.HANDLED'));
            this.loadBillingActions();
            this.loadBillingActionCount();
          },
          error: () => {
            this.message.error(this.translate.instant('BILLING_ACTIONS.ERRORS.UPDATE'));
          },
        });
      },
    });
  }

  reopenBillingAction(item: BillingActionItem): void {
    this.adminDashboardService.reopenBillingAction(item._id).subscribe({
      next: () => {
        this.message.success(this.translate.instant('BILLING_ACTIONS.SUCCESS.REOPENED'));
        this.loadBillingActions();
        this.loadBillingActionCount();
      },
      error: () => {
        this.message.error(this.translate.instant('BILLING_ACTIONS.ERRORS.UPDATE'));
      },
    });
  }

  dismissBillingAction(item: BillingActionItem): void {
    this.modal.confirm({
      nzTitle: this.translate.instant('BILLING_ACTIONS.CONFIRM.DISMISS_TITLE'),
      nzContent: this.translate.instant('BILLING_ACTIONS.CONFIRM.DISMISS_CONTENT'),
      nzOkText: this.translate.instant('BILLING_ACTIONS.ACTIONS.DISMISS'),
      nzOkDanger: true,
      nzOnOk: () => {
        this.adminDashboardService.dismissBillingAction(item._id).subscribe({
          next: () => {
            this.message.success(this.translate.instant('BILLING_ACTIONS.SUCCESS.DISMISSED'));
            this.loadBillingActions();
            this.loadBillingActionCount();
          },
          error: () => {
            this.message.error(this.translate.instant('BILLING_ACTIONS.ERRORS.UPDATE'));
          },
        });
      },
    });
  }

  openBillingActionNoteModal(item: BillingActionItem): void {
    this.noteActionItem = item;
    this.noteText = item.internalNote || '';
    this.noteModalVisible = true;
  }

  closeNoteModal(): void {
    this.noteModalVisible = false;
    this.noteActionItem = null;
    this.noteText = '';
  }

  saveBillingActionNote(): void {
    if (!this.noteActionItem) { return; }
    const note = (this.noteText || '').trim();
    this.adminDashboardService.updateBillingActionNote(this.noteActionItem._id, note).subscribe({
      next: () => {
        this.message.success(this.translate.instant('BILLING_ACTIONS.SUCCESS.NOTE_SAVED'));
        this.closeNoteModal();
        this.loadBillingActions();
      },
      error: () => {
        this.message.error(this.translate.instant('BILLING_ACTIONS.ERRORS.UPDATE'));
      },
    });
  }

  getBillingActionTypeLabel(type: string): string {
    return this.translate.instant(`BILLING_ACTIONS.TYPES.${type}`);
  }

  getBillingActionStatusLabel(status: string): string {
    return this.translate.instant(`BILLING_ACTIONS.STATUS.${status}`);
  }

  get billingActionsTabTitle(): string {
    const base = this.translate.instant('ADMIN_DASHBOARD.TABS.BILLING_ACTIONS');
    const count = this.billingActionCount?.total;
    return count ? `${base} (${count})` : base;
  }

  get contractAlertsTabTitle(): string {
    const base = this.translate.instant('ADMIN_DASHBOARD.TABS.CONTRACT_ALERTS');
    const count = this.contractAlerts.length;
    return count ? `${base} (${count})` : base;
  }

  getNextMilestoneText(milestone?: NextContractMilestone | null): string {
    if (!milestone) {
      return '—';
    }
    return this.translate.instant(milestone.descriptionKey || milestone.labelKey, milestone.params || {});
  }

  getBillingActionStatusColor(status: string): string {
    switch (status) {
      case 'handled': return 'green';
      case 'dismissed': return 'default';
      default: return 'orange';
    }
  }

  openBillingActionCompany(item: BillingActionItem): void {
    const companyId = typeof item.companyId === 'string'
      ? item.companyId
      : (item as any).companyId?._id || (item as any).companyId;
    if (!companyId) {
      this.message.warning('Company ID missing');
      return;
    }
    this.openCompany({
      companyId: String(companyId),
      companyName: item.snapshot.companyName || '',
      plan: item.snapshot.plan || 'free',
      isActive: true,
    } as AdminDashboardCompanyRow);
  }

  openContractAlertCompany(companyId: string): void {
    if (!companyId) {
      return;
    }
    this.openCompany({
      companyId,
      companyName: '',
      plan: 'free',
      isActive: true,
    } as AdminDashboardCompanyRow);
  }

  getBillingActionSummary(item: BillingActionItem): string {
    const s = item.snapshot;
    switch (item.type) {
      case 'property_limit_increased': {
        const oldVal = s.oldMaxProperties ?? 0;
        const newVal = s.newMaxProperties ?? 0;
        const delta = newVal - oldVal;
        const used = s.usedProperties ?? 0;
        return this.translate.instant('BILLING_ACTIONS.SUMMARY.property_limit_increased', {
          old: oldVal, new: newVal, delta: delta > 0 ? `+${delta}` : String(delta), used,
        });
      }
      case 'seat_limit_increased': {
        const oldVal = s.oldSeatLimit ?? 0;
        const newVal = s.newSeatLimit ?? 0;
        const delta = newVal - oldVal;
        const used = s.usedSeats ?? 0;
        return this.translate.instant('BILLING_ACTIONS.SUMMARY.seat_limit_increased', {
          old: oldVal, new: newVal, delta: delta > 0 ? `+${delta}` : String(delta), used,
        });
      }
      case 'special_offer_requested': {
        const count = s.requestedMaxProperties ?? 0;
        const threshold = s.threshold ?? 0;
        return this.translate.instant('BILLING_ACTIONS.SUMMARY.special_offer_requested', { count, threshold });
      }
      case 'upgrade_contract_signed': {
        const plan = s.targetPlan || s.plan || '';
        const props = s.requestedMaxProperties ?? 0;
        const seats = s.requestedSeatLimit ?? 0;
        const startDate = s.contractStartDate
          ? this.formatActionDate(new Date(s.contractStartDate))
          : '';
        if (s.isPendingActivation && startDate) {
          return this.translate.instant('BILLING_ACTIONS.SUMMARY.upgrade_contract_signed_pending', { plan, properties: props, seats, date: startDate });
        }
        if (startDate) {
          return this.translate.instant('BILLING_ACTIONS.SUMMARY.upgrade_contract_signed_active', { plan, properties: props, seats, date: startDate });
        }
        return this.translate.instant('BILLING_ACTIONS.SUMMARY.upgrade_contract_signed', { plan, properties: props, seats });
      }
      case 'business_plan_inquiry': {
        const plan = s.targetPlan || 'business';
        const props = s.requestedMaxProperties ?? 0;
        return this.translate.instant('BILLING_ACTIONS.SUMMARY.business_plan_inquiry', { plan, properties: props });
      }
      default:
        return '';
    }
  }

  private formatActionDate(date: Date): string {
    if (Number.isNaN(date.getTime())) { return ''; }
    return date.toLocaleDateString(this.translate.currentLang === 'de' ? 'de-DE' : 'en-US', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  }
}
